import React, { useState } from 'react';
import {
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  ShieldCheck,
  Heart,
  Download,
  Share2,
  X,
  CreditCard,
  Sparkles,
  Smartphone,
  KeyRound,
  ArrowRight,
  Lock,
  DollarSign,
  AlertCircle,
  Copy,
  Check,
  Star
} from 'lucide-react';
import { BookingDetails } from '../types';
import {
  isDemoCompanionProfile,
  getDemoCompanionBookingMessage,
  DEMO_COMPANION_BOOKING_MESSAGES,
} from '../data/companionsData';
import { useLanguage } from '../i18n/LanguageContext';
import { saveRealMeetup, completeRealMeetup } from '../utils/meetupStorage';
import { saveBookingToFirestore, updateBookingStatusInFirestore, auth } from '../lib/firebase';
import { payV2Booking, getBookingCompletionOtp, verifyBookingCompletionOtp } from '../lib/bookingService';
import { RazorpayCheckout, RazorpayCheckoutResponse } from './RazorpayCheckout';
import { calculatePayout } from '../utils/feeCalculator';

const FUNCTIONS_BASE_URL = String(
  import.meta.env.VITE_FUNCTIONS_BASE_URL || 'https://asia-south1-nifty-cat-7cjpc.cloudfunctions.net'
).replace(/\/+$/, '');

interface BookingConfirmationModalProps {
  booking: BookingDetails | null;
  onClose: () => void;
  onSuccess: () => void;
  onCreditEarnings?: (amount: number, details: { title: string; clientOrBeneficiary: string; meetOtp?: string; serviceCategory?: string }) => void;
}

export const BookingConfirmationModal: React.FC<BookingConfirmationModalProps> = ({
  booking,
  onClose,
  onSuccess,
  onCreditEarnings,
}) => {
  const { language } = useLanguage();
  const [paymentStep, setPaymentStep] = useState<'review' | 'paying' | 'confirmed'>('review');
  const [bookingId, setBookingId] = useState<string>('');
  
  // Companion OTP verification state (server-side SMS delivery + SHA-256 verification)
  const [inputOtp, setInputOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpDeliveryNotice, setOtpDeliveryNotice] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [meetingCompleted, setMeetingCompleted] = useState(false);
  const [verifiedPayoutId, setVerifiedPayoutId] = useState<string>('');
  const [userRating, setUserRating] = useState(5);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [bookingError, setBookingError] = useState<string>('');
  const [checkoutOrder, setCheckoutOrder] = useState<{
    bookingId: string; orderId: string; keyId: string; amount: number; currency: string;
  } | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const processPaymentAndBooking = async (currentBooking: BookingDetails) => {
    setBookingError('');
    setCheckoutOrder(null);

    if (isDemoCompanionProfile(currentBooking.companion)) {
      setBookingError(
        'Not available on this time / date. Not Available for This Booking Time. This companion is a sample profile and cannot accept real bookings. Please choose an available verified companion.'
      );
      setPaymentStep('review');
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setBookingError('Please sign in with your registered account before booking.');
      setPaymentStep('review');
      return;
    }

    setIsProcessing(true);
    setPaymentStep('paying');

    try {
      const idToken = await user.getIdToken(true);
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/createBookingPaymentOrder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            companionId: currentBooking.companion.id,
            bookingDate: currentBooking.date,
            startTime: currentBooking.time,
            durationHours: currentBooking.durationHours,
            locationName: currentBooking.meetingLocation,
            latitude: currentBooking.meeting_location_details?.latitude,
            longitude: currentBooking.meeting_location_details?.longitude,
            googlePlaceId: currentBooking.meeting_location_details?.place_id,
          }),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success !== true ||
          !result.bookingId || !result.orderId ||
          !result.keyId || !Number.isSafeInteger(result.amount) ||
          result.amount <= 0) {
        throw new Error(
          result.error || result.message ||
          `Secure booking order failed (HTTP ${response.status}). No payment was recorded.`
        );
      }

      setBookingId(result.bookingId);
      setCheckoutOrder({
        bookingId: result.bookingId,
        orderId: result.orderId,
        keyId: result.keyId,
        amount: result.amount,
        currency: result.currency || 'INR',
      });
    } catch (err) {
      setCheckoutOrder(null);
      setBookingError(
        err instanceof Error ? err.message :
        'Unable to create a secure booking payment order.'
      );
      setPaymentStep('review');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVerifiedRazorpayPayment = async (
    payment: RazorpayCheckoutResponse
  ) => {
    const order = checkoutOrder;
    const user = auth.currentUser;

    if (!order || !user) {
      setBookingError('Payment verification context is missing. Contact support before retrying.');
      setPaymentStep('review');
      return;
    }

    setBookingError('');
    setIsProcessing(true);

    try {
      const idToken = await user.getIdToken(true);
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/verifyBookingPayment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify(payment),
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.success !== true ||
          result.paymentStatus !== 'SUCCESS' ||
          result.bookingStatus !== 'CONFIRMED' ||
          result.bookingId !== order.bookingId) {
        throw new Error(
          result.error || result.message ||
          'Server could not verify this payment. Do not retry payment blindly; check your payment status first.'
        );
      }

      setBookingId(result.bookingId);
      setCheckoutOrder(null);
      setPaymentStep('confirmed');
      onSuccess();
    } catch (err) {
      setBookingError(
        err instanceof Error ? err.message :
        'Payment verification failed. Booking has not been confirmed by this screen.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayNow = () => {
    if (booking) {
      processPaymentAndBooking(booking);
    }
  };

  const handleRequestCompletionOtp = async () => {
    setOtpError('');
    setOtpDeliveryNotice('');
    const user = auth.currentUser;
    if (!user || !bookingId) {
      setOtpError('Please sign in with the client account that created this booking.');
      return;
    }
    setIsSendingOtp(true);
    try {
      const res = await getBookingCompletionOtp(bookingId, user.uid);
      if (!res.success) {
        setOtpError(res.error || 'Unable to send meetup verification OTP.');
        return;
      }
      setOtpDeliveryNotice(
        res.message || 'Meetup verification OTP sent via SMS to the client’s verified mobile number.'
      );
    } catch (err: any) {
      setOtpError(err.message || 'Failed to send meetup verification OTP.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyCompanionOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    const user = auth.currentUser;
    const cleanOtp = inputOtp.trim();
    if (!user || !bookingId) {
      setOtpError('Please sign in with the assigned companion account to verify the meetup OTP.');
      return;
    }
    if (!/^\d{6}$/.test(cleanOtp)) {
      setOtpError('Please enter a valid 6-digit meetup verification OTP.');
      return;
    }
    setIsVerifyingOtp(true);
    try {
      const res = await verifyBookingCompletionOtp(bookingId, user.uid, cleanOtp);
      if (!res.success) {
        setOtpError(res.error || 'OTP verification failed.');
        setMeetingCompleted(false);
        return;
      }
      setVerifiedPayoutId(res.payout?.id || `PAYOUT-${bookingId}`);
      setMeetingCompleted(true);
      if (onCreditEarnings && booking) {
        const payoutCalc = calculatePayout(booking.totalAmount);
        onCreditEarnings(payoutCalc.companionPayout, {
          title: `Booking #${bookingId}`,
          clientOrBeneficiary: booking.companion.name,
          serviceCategory: booking.occasion,
        });
      }
    } catch (err: any) {
      setOtpError(err.message || 'Failed to verify meetup OTP.');
      setMeetingCompleted(false);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  if (!booking) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 animate-in fade-in">
      <div className="relative bg-white rounded-3xl max-w-xl w-full overflow-hidden shadow-2xl border border-pink-100 p-5 sm:p-7 max-h-[92vh] overflow-y-auto">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Step 1: Review & Select Payment */}
        {paymentStep === 'review' && (
          <div className="space-y-5">
            <div>
              <span className="text-xs font-bold text-pink-600 uppercase tracking-wider">Step 3 of 4: Checkout</span>
              <h2 className="text-2xl font-extrabold text-slate-900 mt-0.5">
                Confirm & Secure Payment
              </h2>
              <p className="text-xs text-slate-500">
                Escrow Guarantee: Funds remain locked in secure escrow. The companion receives payment only after you meet in person and share your OTP.
              </p>
            </div>

            {/* Error Banner (Double Booking / Conflict) */}
            {bookingError && (
              <div className="p-3.5 bg-rose-50 border border-rose-300 rounded-2xl flex items-start gap-2.5 text-xs text-rose-800 animate-in fade-in">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-bold block text-rose-900">Booking Conflict Detected</span>
                  <span className="text-rose-700 block mt-0.5">{bookingError}</span>
                </div>
              </div>
            )}

            {/* Companion summary banner */}
            <div className="flex items-center gap-3.5 p-3.5 rounded-2xl bg-slate-50 border border-slate-100">
              <img
                src={booking.companion.image}
                alt={booking.companion.name}
                className="w-14 h-14 rounded-2xl object-cover shrink-0"
                referrerPolicy="no-referrer"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-slate-900 text-sm truncate">
                    {booking.companion.name}, {booking.companion.age}
                  </h4>
                  <span className="text-sm font-extrabold text-pink-600 ml-2">
                    ₹{booking.totalAmount}
                  </span>
                </div>
                <p className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
                  <span>📅 {booking.date}</span>
                  <span>⏰ {booking.time}</span>
                  <span>⏳ {booking.durationHours} hrs</span>
                </p>
                <p className="text-xs text-purple-600 font-medium mt-0.5 truncate">
                  📍 {booking.meetingLocation}, {booking.city}
                </p>
              </div>
            </div>

            {/* Escrow Badge Notice */}
            <div className="p-3 bg-amber-50/80 border border-amber-200 rounded-2xl flex items-start gap-2.5 text-xs text-amber-900">
              <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold block">100% Escrow & OTP Protection Guarantee</span>
                <span className="text-amber-800 text-[11px] leading-tight block mt-0.5">
                  Your advance payment is held securely in the platform's Escrow Vault. After physically meeting your companion, sharing your secret 6-digit SMS OTP releases the payout.
                </span>
              </div>
            </div>

            {/* Official Razorpay Payment Gateway Summary */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">
                    Official Razorpay Secure Checkout
                  </span>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                  256-Bit Encrypted
                </span>
              </div>

              <div className="space-y-1.5 text-xs text-slate-600">
                <div className="flex justify-between">
                  <span>Total Booking Amount</span>
                  <span className="font-bold text-slate-900">₹{booking.totalAmount}</span>
                </div>
                <div className="flex justify-between text-[11px] text-slate-500">
                  <span>Companion Escrow Allocation (85%)</span>
                  <span>₹{calculatePayout(booking.totalAmount).companionPayout}</span>
                </div>
                <div className="flex justify-between text-[11px] text-slate-500">
                  <span>Platform Security & Escrow Fee (15%)</span>
                  <span>₹{calculatePayout(booking.totalAmount).platformFee}</span>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 leading-relaxed">
                Supports UPI (Google Pay, PhonePe, Paytm), Credit/Debit Cards, and NetBanking directly inside the official Razorpay Checkout modal with server-side HMAC signature verification.
              </p>

              <button
                type="button"
                disabled={isProcessing}
                onClick={handlePayNow}
                className="w-full py-3.5 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 disabled:opacity-60 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                <span>Proceed to Secure Razorpay Checkout (₹{booking.totalAmount})</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Processing state */}
        {paymentStep === 'paying' && (
          <div className="py-8 text-center space-y-4">
            {checkoutOrder ? (
              <>
                <h3 className="text-lg font-bold text-slate-900">Complete secure booking payment</h3>
                <p className="text-sm text-slate-600">
                  Amount: ₹{(checkoutOrder.amount / 100).toFixed(2)}.
                  The booking will be confirmed only after server-side payment verification.
                </p>
                <RazorpayCheckout
                  orderId={checkoutOrder.orderId}
                  keyId={checkoutOrder.keyId}
                  amountPaise={checkoutOrder.amount}
                  currency={checkoutOrder.currency}
                  description="PairUpToday booking"
                  customerName={auth.currentUser?.displayName || ''}
                  customerEmail={auth.currentUser?.email || ''}
                  onPaymentResponse={handleVerifiedRazorpayPayment}
                  onDismiss={() => {
                    setCheckoutOrder(null);
                    setPaymentStep('review');
                  }}
                  onError={(message) => {
                    setBookingError(message);
                    setCheckoutOrder(null);
                    setPaymentStep('review');
                  }}
                />
                {isProcessing && (
                  <p className="text-xs text-slate-500">Verifying payment securely…</p>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <div className="w-16 h-16 rounded-full border-4 border-pink-500 border-t-transparent animate-spin mx-auto" />
                <h3 className="text-lg font-bold text-slate-900">Creating secure payment order…</h3>
                <p className="text-xs text-slate-500">No booking confirmation is shown until payment verification succeeds.</p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Success Confirmation Ticket & OTP Payment Release System */}
        {paymentStep === 'confirmed' && (
          <div className="space-y-5 animate-in zoom-in-95 duration-300">
            
            {/* Top Status Header */}
            <div className="text-center space-y-1">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto shadow-md transition-all ${
                meetingCompleted ? 'bg-emerald-100 text-emerald-600' : 'bg-pink-100 text-pink-600'
              }`}>
                {meetingCompleted ? (
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                ) : (
                  <ShieldCheck className="w-8 h-8 text-pink-600" />
                )}
              </div>

              <div className="pt-1">
                <span className={`text-[11px] font-extrabold uppercase tracking-widest px-3 py-0.5 rounded-full inline-block ${
                  meetingCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'
                }`}>
                  {meetingCompleted ? 'Meeting Completed • Payout Pending' : 'Booking Confirmed • Payout Pending'}
                </span>
                <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
                  {meetingCompleted ? 'Meeting Completed' : 'Booking Confirmed'}
                </h2>
                <p className="text-xs text-slate-500">
                  {meetingCompleted
                    ? 'Meeting completion recorded. Payout is still pending provider confirmation.'
                    : `Your meetup with Companion ${booking.companion.name} is confirmed. Payout has not been released.`}
                </p>
              </div>
            </div>

            {/* Meetup Pass Card */}
            <div className="bg-gradient-to-br from-pink-50/90 via-purple-50/70 to-pink-50/90 rounded-2xl p-4 sm:p-5 border border-pink-200 text-left space-y-3.5 shadow-sm">
              
              <div className="flex justify-between items-center pb-2.5 border-b border-pink-200/80">
                <div>
                  <p className="text-[10px] uppercase font-bold text-slate-400">Booking Pass ID</p>
                  <p className="text-sm font-extrabold text-slate-900">{bookingId}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase font-bold text-slate-400">Escrow Amount</p>
                  <p className="text-sm font-extrabold text-emerald-700">₹{booking.totalAmount}</p>
                </div>
              </div>

              {/* Secret Meet Completion OTP Box for Client (SMS Delivery Only) */}
              <div className="p-3.5 bg-white rounded-2xl border-2 border-dashed border-pink-300 shadow-xs space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <KeyRound className="w-4 h-4 text-pink-600" />
                      <p className="text-xs font-extrabold text-slate-900">
                        Secure SMS Meetup Verification
                      </p>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      6-digit completion code is delivered only via SMS to your verified phone number.
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={isSendingOtp || !bookingId}
                    onClick={handleRequestCompletionOtp}
                    className="px-3 py-1.5 rounded-xl bg-pink-600 hover:bg-pink-700 disabled:bg-pink-300 text-white text-xs font-extrabold transition-colors cursor-pointer shrink-0"
                  >
                    {isSendingOtp ? 'Sending SMS...' : 'Send SMS OTP'}
                  </button>
                </div>
                {otpDeliveryNotice && (
                  <p className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-2.5 py-1.5">
                    {otpDeliveryNotice}
                  </p>
                )}
              </div>

              {/* Venue & Time details */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-slate-400 text-[10px] font-semibold">DATE & TIME</p>
                  <p className="font-bold text-slate-800">{booking.date} at {booking.time}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-[10px] font-semibold">DURATION & OCCASION</p>
                  <p className="font-bold text-slate-800">{booking.durationHours} Hours • {booking.occasion}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-400 text-[10px] font-semibold">MEETING VENUE</p>
                  <p className="font-bold text-slate-800 truncate">📍 {booking.meetingLocation}, {booking.city}</p>
                </div>
                <div className="col-span-2 pt-1 flex items-center justify-between">
                  <span className="text-slate-500 text-[10px] font-mono">1-Click Directions:</span>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=22.62,88.46"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-sm transition-all"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    <span>Open in Google Maps</span>
                  </a>
                </div>
              </div>

              {/* V2 Payout & Platform Fee Transparency (Rule 18 & 19) */}
              {/* Fee Breakdown using fee_bps / 10000 */}
              {(() => {
                const payoutData = calculatePayout(booking.totalAmount);
                return (
                  <div className="p-3 bg-purple-50/80 rounded-2xl border border-purple-200 text-xs space-y-1 font-mono">
                    <div className="flex justify-between text-slate-600 text-[11px]">
                      <span>Gross Booking Amount:</span>
                      <span className="font-bold text-slate-800">₹{payoutData.grossAmount}</span>
                    </div>
                    <div className="flex justify-between text-purple-700 text-[11px]">
                      <span>Platform Fee ({payoutData.feePercentage}% Safety & Escrow):</span>
                      <span>₹{payoutData.platformFee}</span>
                    </div>
                    <div className="flex justify-between text-emerald-700 font-bold border-t border-purple-200 pt-1">
                      <span>Companion Net Payout ({100 - payoutData.feePercentage}%):</span>
                      <span>₹{payoutData.companionPayout}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Escrow Rule Explanatory Banner in English */}
            <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 text-left space-y-1 text-xs">
              <div className="flex items-center gap-1.5 font-bold text-slate-800">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <span>OTP Sharing & Payout Release Guidelines:</span>
              </div>
              <ul className="text-[11px] text-slate-600 space-y-1 list-disc list-inside leading-tight pl-0.5">
                <li>No payment is released before client and companion meet in person.</li>
                <li>Once the meeting concludes satisfactorily, the client shares the 6-digit SMS OTP with the companion.</li>
                <li>Server-side OTP verification marks the meetup completed and creates a pending payout record awaiting provider confirmation.</li>
              </ul>
            </div>

            {/* ================= COMPANION MEETING VERIFICATION ================= */}
            {!meetingCompleted ? (
              <div className="p-4 bg-purple-50/70 border border-purple-200 rounded-2xl text-left space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-purple-600 text-white flex items-center justify-center font-bold text-xs">
                      👩
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-purple-950">
                        Companion Meetup OTP Verification
                      </h4>
                      <p className="text-[10px] text-purple-700">
                        Enter the 6-digit SMS code shared by the client to verify meetup completion.
                      </p>
                    </div>
                  </div>
                  <span className="text-[10px] bg-purple-200 text-purple-900 font-bold px-2 py-0.5 rounded-md">
                    Payout: Escrowed
                  </span>
                </div>

                <form onSubmit={handleVerifyCompanionOtp} className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="Enter 6-digit SMS OTP"
                      disabled={isVerifyingOtp || !bookingId}
                      value={inputOtp}
                      onChange={(e) => {
                        setInputOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                        setOtpError('');
                      }}
                      className="flex-1 px-3 py-2 bg-white border border-purple-300 rounded-xl text-center text-sm font-extrabold tracking-widest text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono shadow-xs"
                    />
                    <button
                      type="submit"
                      disabled={isVerifyingOtp || !bookingId || inputOtp.trim().length !== 6}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white text-xs font-extrabold rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
                    >
                      {isVerifyingOtp ? (
                        <span>Verifying...</span>
                      ) : (
                        <>
                          <span>Verify Meetup OTP</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </div>

                  {otpError && (
                    <p className="text-xs text-rose-600 font-bold flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {otpError}
                    </p>
                  )}
                </form>
              </div>
            ) : (
              /* Meetup Verified — Payout Pending Provider Confirmation Card */
              <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 text-left space-y-3 animate-in fade-in">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-extrabold text-emerald-900 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Meetup OTP Verified for {booking.companion.name}
                    </span>
                    <p className="text-[11px] text-emerald-700 mt-0.5">
                      Payout Ref: {verifiedPayoutId || `PAYOUT-${bookingId}`} • Payout pending provider confirmation
                    </p>
                  </div>
                  <span className="text-xs font-bold text-amber-800 bg-amber-100 px-2 py-1 rounded-lg">
                    VERIFIED_PENDING_PAYOUT
                  </span>
                </div>

                {/* Rating Prompt for Client */}
                {!reviewSubmitted ? (
                  <div className="p-3 bg-white rounded-xl border border-emerald-100 space-y-2">
                    <p className="text-xs font-bold text-slate-800">
                      How was your experience with {booking.companion.name}?
                    </p>
                    <div className="flex items-center gap-1.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setUserRating(star)}
                          className="p-1 text-amber-400 hover:scale-110 transition-transform cursor-pointer"
                        >
                          <Star
                            className={`w-5 h-5 ${
                              star <= userRating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'
                            }`}
                          />
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setReviewSubmitted(true)}
                        className="ml-auto px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                      >
                        Submit Review
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs font-bold text-emerald-800 bg-emerald-100/70 p-2.5 rounded-xl flex items-center gap-1.5">
                    <Heart className="w-4 h-4 fill-emerald-600 text-emerald-600" />
                    <span>Thank you for reviewing {booking.companion.name}!</span>
                  </div>
                )}
              </div>
            )}

            {/* Done CTA */}
            <button
              onClick={() => {
                onSuccess();
                onClose();
              }}
              className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-sm rounded-2xl shadow-md transition-all cursor-pointer"
            >
              Done & Return to Explore
            </button>
          </div>
        )}

      </div>
    </div>
  );
};


